import { EquipmentIcon, PinIcon } from "@xola/icons";
import { Avatar, Logo } from "@xola/ui-kit";
import { DateFormat, formatDate } from "common";
import React from "react";
import { ExperiencePurchaseItem } from "types";
import shallow from "zustand/shallow";
import { getGuestsByStatus } from "../PurchaseModification/CheckInPurchaseGuests/CheckInHelper";
import { useDashboardTableStore } from "./DashboardTable/useDashboardTableStore";
import { isUngroupedTablePrintView, useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardGroupBy } from "@/types/Dashboard";

interface DashboardTableGroupHeaderProps {
    purchaseItems: ExperiencePurchaseItem[];
    groupId?: string;
    isPrint?: boolean;
}

export const DashboardTableGroupHeader = ({
    purchaseItems,
    groupId,
    isPrint = false,
}: DashboardTableGroupHeaderProps) => {
    const reservationsCount = purchaseItems?.length;
    let guestsCount = 0;
    let noShowCount = 0;
    const events = new Set<string>();
    if (purchaseItems)
        for (const item of purchaseItems) {
            const { noshow } = getGuestsByStatus(item);
            noShowCount += noshow;
            guestsCount += item.quantity - noshow;
            if (item.event.id) {
                events.add(item.event.id);
            }
        }
    const eventsCount = events.size;
    const [groupBy, date] = useDashboardFilterStore((state) => [state.filters.groupBy, state.date], shallow);
    const isPrintingUngroupedTable = isUngroupedTablePrintView(isPrint);
    const groupHeaderData = useDashboardTableStore((state) => state.groupHeaderData);

    return (
        <h6 className="mb-2 flex !h-6 items-center font-bold" data-testid="dashboard-table-header">
            {isPrintingUngroupedTable ? (
                <span className="text-bold flex items-center space-x-2 text-lg">
                    {formatDate(date, DateFormat.DATE)}
                </span>
            ) : (
                groupId &&
                groupBy && (
                    <GroupTitleWithImage
                        title={groupHeaderData[groupId]?.name}
                        imageUrl={groupHeaderData[groupId]?.imageData}
                        groupId={groupId}
                    />
                )
            )}
            <div className="ml-6 flex items-center space-x-2">
                {groupBy !== DashboardGroupBy.EVENT && (
                    <>
                        <span data-testid="evets-count">{eventsCount} Events</span>
                        <span className="text-3xl opacity-20">&middot;</span>
                    </>
                )}

                <span data-testid="reservations-count">{reservationsCount} Reservations</span>

                <span className="text-3xl opacity-20">&middot;</span>
                <span data-testid="guests-count">
                    {guestsCount} Guests {noShowCount > 0 && ` (+${noShowCount} No Show)`}
                </span>
            </div>
        </h6>
    );
};

const GroupTitleWithImage = ({ groupId, title, imageUrl }: { groupId: string; title: string; imageUrl?: string }) => {
    let image;
    const groupBy = useDashboardFilterStore((state) => state.filters.groupBy);
    switch (groupBy) {
        case DashboardGroupBy.GUIDE:
            if (groupId === "No Guide") {
                break;
            }
            image = imageUrl ? (
                <Logo src={imageUrl} className="h-6 !w-6" />
            ) : (
                <Avatar color="bg-primary-lighter" name={title} size="tiny" className="no-translate !h-6 !w-6" />
            );
            break;
        case DashboardGroupBy.EVENT:
        case DashboardGroupBy.PRODUCT:
            image = <Logo src={imageUrl} className="h-6 !w-6" />;
            break;
        case DashboardGroupBy.EQUIPMENT:
            if (groupId === "No Equipment") {
                break;
            }
            image = <EquipmentIcon size="medium" />;
            break;
        case DashboardGroupBy.PICKUP:
            if (groupId === "No Pickup") {
                break;
            }
            image = <PinIcon size="medium" />;
            break;
    }

    return (
        <div className="flex items-center space-x-2">
            {image}
            <span className="text-bold text-lg" data-testid="table-title">
                {title}
            </span>
        </div>
    );
};
